import { AssignCommunitiesTable, Button, Textfield } from '@flexo/atoms';
import {
  getPrimaryOrigin,
  httpService,
  IconWrapper,
  REGEX,
} from '@flexo/general';
import { DrawerCloseButton } from '../drawer-close-button';
import {
  ALERT,
  alertCallbackStore,
  METADATA,
  RIGHT_DRAWER,
} from '@flexo/reducers';
import { Player } from '@lottiefiles/react-lottie-player';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styles from './right-drawer-content.module.scss';

const loader = '/loaderLottie.json';

const ENV = (import.meta as any).env;

// Role hierarchy: higher number = higher privilege
const ROLE_HIERARCHY: Record<string, number> = {
  enduser: 1,
  supervisor: 2,
  administrator: 3,
};

// Map display role to internal role value
const mapDisplayRoleToInternal = (displayRole: string): string => {
  const roleMap: Record<string, string> = {
    Admin: 'administrator',
    'Community Supervisor': 'supervisor',
    'End User': 'enduser',
    administrator: 'administrator',
    supervisor: 'supervisor',
    enduser: 'enduser',
  };
  return roleMap[displayRole] || 'enduser';
};

export function AdminUpdateUserDrawerContent() {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const footerRef = useRef<HTMLDivElement>(null);

  // Keep footer width in sync with the drawer parent
  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) return;
    const drawer = footer.closest('[class*="RightDrawer__AdminUser"]') as HTMLElement | null;
    if (!drawer) return;

    const sync = () => {
      footer.style.width = `${drawer.offsetWidth}px`;
    };
    sync();

    const ro = new ResizeObserver(sync);
    ro.observe(drawer);
    return () => ro.disconnect();
  }, []);

  const metadata = useSelector((state: any) => state.metadata);
  const rightDrawer = useSelector((state: any) => state.rightDrawer);

  // Get user data passed from table row click
  const currentAdmin = useMemo(
    () => rightDrawer?.data || {},
    [rightDrawer?.data]
  );

  // Get user's original role (for determining which roles can be selected)
  const originalRole = useMemo(() => {
    return mapDisplayRoleToInternal(currentAdmin?.role);
  }, [currentAdmin?.role]);

  // Get original email for change detection
  const originalEmail = useMemo(() => {
    return currentAdmin?.email || '';
  }, [currentAdmin?.email]);

  const isEmailChanged = () => {
    return email.trim().toLowerCase() !== originalEmail.trim().toLowerCase();
  };

  const resetEmail = () => {
    setEmail(originalEmail);
  };

  // Initialize state from passed user data
  const [role, setRole] = useState<string>(originalRole);
  const [email, setEmail] = useState<string>(currentAdmin?.email || '');
  const [search, setSearch] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Initialize selected communities from user's assigned communities
  const [selectedCommunities, setSelectedCommunities] = useState<Set<string>>(
    () => {
      const communityDetails = currentAdmin?._raw?.communityDetails || [];
      return new Set(communityDetails.map((c: any) => c.communityId));
    }
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isResending, setIsResending] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<boolean>(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>('');

  const isInvited = currentAdmin?.state === 'invited';

  // Update state when drawer data changes
  useEffect(() => {
    if (currentAdmin?.email) {
      setEmail(currentAdmin.email);
      const mappedRole = mapDisplayRoleToInternal(currentAdmin?.role);
      setRole(mappedRole);
      const communityDetails = currentAdmin?._raw?.communityDetails || [];
      setSelectedCommunities(
        new Set(communityDetails.map((c: any) => c.communityId))
      );
    }
  }, [currentAdmin]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fullName = useMemo(() => {
    const display =
      (currentAdmin?.user as string) ||
      (currentAdmin?.displayName as string) ||
      (currentAdmin?.fullName as string) ||
      [currentAdmin?._raw?.firstName, currentAdmin?._raw?.lastName]
        .filter(Boolean)
        .join(' ');
    return display || '';
  }, [currentAdmin]);

  // Check if a role can be selected (promotions and admin→supervisor demotion allowed)
  const canSelectRole = (_targetRole: string): boolean => {
    return true;
  };

  function handleEmailBlur() {
    const trimmed = email.trim();
    if (!trimmed || trimmed === originalEmail) {
      setEmailError(false);
      setEmailErrorMessage('');
      return;
    }
    if (!REGEX.email.test(trimmed)) {
      setEmailError(true);
      setEmailErrorMessage(
        t('drawers.adminUpdateUser.email.invalidEmail') as string
      );
    }
  }

  function handleEmailFocus() {
    setEmailError(false);
    setEmailErrorMessage('');
  }

  function closeDrawer() {
    dispatch(RIGHT_DRAWER.HideDrawer());
  }

  async function handleResendInvitation() {
    const userId =
      currentAdmin?.userId ||
      currentAdmin?.clerkUserId ||
      currentAdmin?._raw?.userId ||
      currentAdmin?._raw?.clerkUserId;
    const organizationId = ENV.VITE_APP_ORG_ID;

    if (!userId || isResending) return;

    setIsResending(true);

    const locale = i18n.language?.includes('_')
      ? i18n.language.split('_')[1]
      : i18n.language || 'en';

    try {
      await dispatch(
        METADATA.ResendInvitationAsync({
          dispatch,
          organizationId,
          userId,
          locale,
          language: locale,
          inviteUrl: `${getPrimaryOrigin()}/register-by-invite?email=${encodeURIComponent(
            currentAdmin?.email || ''
          )}`,
        }) as any
      );
    } finally {
      setIsResending(false);
    }
  }

  // Handle save - update role and/or communities
  async function handleSubmit() {
    if (isSubmitting) return;

    // userId is at root level, not in _raw. For admin-only users (Clerk), use clerkUserId.
    const userId =
      currentAdmin?.userId ||
      currentAdmin?.clerkUserId ||
      currentAdmin?._raw?.userId ||
      currentAdmin?._raw?.clerkUserId;
    if (!userId) {
      console.error('Cannot update: userId not found', currentAdmin);
      return;
    }

    setIsSubmitting(true);

    try {
      const organizationId = ENV.VITE_APP_ORG_ID;
      let hasChanges = false;

      // Check if email changed (never for invited users - backend rejects it)
      const emailChanged = !isInvited && email.trim() !== originalEmail.trim();

      // Check if role changed (promotion) - use v1_4 PATCH endpoint
      if (role !== originalRole) {
        hasChanges = true;
        await httpService.patch({
          url: `${ENV.VITE_APP_BASE_URL}/community/catalog/v1_4/organization/${organizationId}/users/${userId}`,
          data: {
            role: role,
            community_ids:
              role === 'supervisor'
                ? Array.from(selectedCommunities)
                : undefined,
          },
          mock: false,
        });
      } else if (role === 'supervisor') {
        // Check if communities changed (for supervisor)
        const originalCommunities = new Set(
          (currentAdmin?._raw?.communityDetails || []).map(
            (c: any) => c.communityId
          )
        );
        const communitiesChanged =
          selectedCommunities.size !== originalCommunities.size ||
          ![...selectedCommunities].every((id) => originalCommunities.has(id));

        if (communitiesChanged) {
          hasChanges = true;
          // v1_4 endpoint uses clerkUserId in path
          await httpService.put({
            url: `${ENV.VITE_APP_BASE_URL}/community/catalog/v1_4/organization/${organizationId}/users/${userId}/communities`,
            data: {
              communityIds: Array.from(selectedCommunities),
            },
            mock: false,
          });
        }
      }

      // Update email if changed - use v1_4 PATCH endpoint
      if (emailChanged) {
        hasChanges = true;
        const emailResponse = await httpService.patch({
          url: `${ENV.VITE_APP_BASE_URL}/community/catalog/v1_4/organization/${organizationId}/users/${userId}`,
          data: {
            email: email.trim(),
          },
          mock: false,
        });

        // Detect "email already in use" from backend error code
        if (emailResponse.status === 400) {
          const detail = emailResponse.data?.detail || '';
          if (typeof detail === 'string' && detail.includes('ERR_EMAIL_005')) {
            dispatch(
              ALERT.SetAlert({
                type: 'BANNER_EMAIL_ALREADY_IN_USE',
              } as any)
            );
            return;
          }
        }

        if (emailResponse.status !== 200) {
          throw new Error('Failed to update email');
        }
      }

      // Only close drawer and show success if there were actual changes
      if (hasChanges) {
        // Refresh both paginated tables with fresh data before closing
        await Promise.all([
          dispatch(
            METADATA.SetAdminsPaginatedAsync({
              dispatch,
              organizationId,
              status: 'active',
              page: 1,
              pageSize: 50,
              sortBy: 'user',
              sortDir: 'asc',
              isRefresh: true,
            }) as any
          ),
          dispatch(
            METADATA.SetAdminsPaginatedAsync({
              dispatch,
              organizationId,
              status: 'invited',
              page: 1,
              pageSize: 50,
              sortBy: 'invited',
              sortDir: 'desc',
              isRefresh: true,
            }) as any
          ),
        ]);
        // Show success alert
        dispatch(
          ALERT.SetAlert({
            type: 'BANNER_USER_UPDATE_SUCCESS',
          } as any)
        );
        closeDrawer();
      } else {
        // No changes detected - just close quietly
        closeDrawer();
      }
    } catch (err: any) {
      console.error('Error updating user:', err);
      dispatch(
        ALERT.SetAlert({
          type: 'BANNER_UNEXPECTED_ERROR',
        } as any)
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  const allCommunities: any[] = useMemo(() => {
    const fromEntities = (metadata?.entities as any)?.community || [];
    const fromSlice = metadata?.communities || [];
    return (fromEntities?.length ? fromEntities : fromSlice) || [];
  }, [metadata]);

  const sites: any[] = useMemo(
    () => (metadata?.entities as any)?.site || [],
    [metadata]
  );
  const members: any[] = useMemo(
    () => (metadata?.entities as any)?.member || [],
    [metadata]
  );

  const countsByCommunity = useMemo(() => {
    const siteCount = new Map<string, number>();
    const memberCount = new Map<string, number>();
    sites.forEach((s: any) => {
      const key = s?.communityId;
      if (!key) return;
      siteCount.set(key, (siteCount.get(key) || 0) + 1);
    });
    members.forEach((m: any) => {
      const key = m?.communityId;
      if (!key) return;
      memberCount.set(key, (memberCount.get(key) || 0) + 1);
    });
    return { siteCount, memberCount };
  }, [sites, members]);

  const filteredCommunities = useMemo(() => {
    const s = (search || '').toLowerCase().trim();
    if (!s) return allCommunities;
    return allCommunities.filter((c: any) => {
      const name = (c?.communityName || '').toLowerCase();
      const code = (c?.communityCode || c?.communityId || '').toLowerCase();
      const extId = (c?.communityExternalId || '').toLowerCase();
      return name.includes(s) || code.includes(s) || extId.includes(s);
    });
  }, [allCommunities, search]);

  const allVisibleIds = useMemo(
    () => filteredCommunities.map((c: any) => c?.communityId).filter(Boolean),
    [filteredCommunities]
  );
  const isAllSelected =
    allVisibleIds.length > 0 &&
    allVisibleIds.every((id) => selectedCommunities.has(id));

  function toggleSelectAll() {
    if (isAllSelected) {
      const next = new Set(selectedCommunities);
      allVisibleIds.forEach((id) => next.delete(id));
      setSelectedCommunities(next);
    } else {
      const next = new Set(selectedCommunities);
      allVisibleIds.forEach((id) => next.add(id));
      setSelectedCommunities(next);
    }
  }

  function toggleRow(id: string) {
    const next = new Set(selectedCommunities);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedCommunities(next);
  }

  /**
   * Handle range toggle for shift+click selection
   * @param ids - Array of community IDs in the range
   * @param select - Whether to select or deselect the range
   */
  function toggleRange(ids: string[], select: boolean) {
    const next = new Set(selectedCommunities);
    ids.forEach((id) => {
      if (select) {
        next.add(id);
      } else {
        next.delete(id);
      }
    });
    setSelectedCommunities(next);
  }

  // Detect whether any field has been modified compared to original data
  const hasChanges = useMemo(() => {
    // Email changed (only for non-invited users)
    if (!isInvited && email.trim().toLowerCase() !== originalEmail.trim().toLowerCase()) return true;
    // Role changed
    if (role !== originalRole) return true;
    // Communities changed (only relevant for supervisor)
    if (role === 'supervisor') {
      const originalCommunities = new Set(
        (currentAdmin?._raw?.communityDetails || []).map((c: any) => c.communityId)
      );
      if (selectedCommunities.size !== originalCommunities.size) return true;
      for (const id of selectedCommunities) {
        if (!originalCommunities.has(id)) return true;
      }
    }
    return false;
  }, [email, originalEmail, role, originalRole, selectedCommunities, currentAdmin, isInvited]);

  // Determine what to show based on selected role
  const showCommunitiesTable = role === 'supervisor';
  const showAdminMessage = role === 'administrator';
  const showEnduserMessage = role === 'enduser';

  // Role options with titles and descriptions for dropdown
  const roleOptions = [
    // {
    //   value: 'enduser',
    //   title: t('drawers.adminUpdateUser.roles.enduser.label', {
    //     defaultValue: 'End User',
    //   }),
    //   description: t('drawers.adminUpdateUser.roles.enduser.description', {
    //     defaultValue: 'End user with limited access',
    //   }),
    //   disabled: !canSelectRole('enduser'),
    // },
    {
      value: 'supervisor',
      title: t('drawers.adminUpdateUser.roles.supervisor.label', {
        defaultValue: 'Supervisor',
      }),
      description: t('drawers.adminUpdateUser.roles.supervisor.description', {
        defaultValue: 'Supervisor with access to assigned communities',
      }),
      disabled: !canSelectRole('supervisor'),
    },
    {
      value: 'administrator',
      title: t('drawers.adminUpdateUser.roles.administrator.label', {
        defaultValue: 'Administrator',
      }),
      description: t(
        'drawers.adminUpdateUser.roles.administrator.description',
        { defaultValue: 'Full access to all features' }
      ),
      disabled: false, // Administrator is always selectable (highest role)
    },
  ];

  const selectedRoleOption = roleOptions.find((opt) => opt.value === role);

  function handleSelectRole(value: string) {
    setRole(value);
    setIsDropdownOpen(false);
  }

  return (
    <>
      <div style={{ position: 'relative' }}>
        <div className={styles.AdminUser__Title__Wrapper}>
          <div className={styles.AdminUser__Title}>
            <p>
              {isInvited
                ? t('drawers.adminUpdateUser.titleInvited', {
                    defaultValue: 'Dettaglio utente invitato',
                  })
                : t('drawers.adminUpdateUser.title', {
                    defaultValue: 'Modifica profilo admin',
                  })}{' '}
              <span>{fullName ? ` ${fullName}` : ''}</span>
            </p>
            <DrawerCloseButton onClose={closeDrawer} ariaLabel={t('common.close', { defaultValue: 'Close' })} />
          </div>
          <p className={styles.AdminUser__Subtitle}>
            {isInvited
              ? t('drawers.adminUpdateUser.subtitle.beforeLinkInvited', {
                  defaultValue: 'Edit or cancel this invite.',
                })
              : t('drawers.adminUpdateUser.subtitle.beforeLink', {
                  defaultValue: 'Modifica o elimina questo utente.',
                })}
          </p>
        </div>

        <div className={styles.AdminUser__Body}>
          {/* Email - editable */}
          <Textfield
            textfieldColor="white"
            variant="default"
            name="email"
            value={email}
            placeholder="Email"
            label="Email"
            type="email"
            error={emailError}
            errorDescription={emailErrorMessage}
            onChange={(e: any) => !isInvited && setEmail(e.target.value)}
            onBlur={handleEmailBlur}
            onFocus={handleEmailFocus}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
            autoComplete="off"
            disabled={isInvited}
            icon={!isInvited && isEmailChanged() ? 'refresh' : undefined}
            onIconClick={
              !isInvited && isEmailChanged() ? resetEmail : undefined
            }
          />

          {/* Role select - custom dropdown with title + description */}
          <div className={styles.AdminUser__Select} ref={dropdownRef}>
            <span className={'heading4'}>
              {t('drawers.adminUpdateUser.inputs.role.label', {
                defaultValue: 'Ruolo',
              })}
            </span>
            <div
              className={styles.AdminUser__Select__Container}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <span
                className={
                  role
                    ? styles.AdminUser__Select__Value
                    : styles.AdminUser__Select__Placeholder
                }
              >
                {selectedRoleOption?.title ||
                  t('drawers.adminUpdateUser.inputs.role.placeholder', {
                    defaultValue: 'Seleziona un ruolo',
                  })}
              </span>
              <div
                className={`${styles.AdminUser__Select__Icon} ${
                  isDropdownOpen ? styles['AdminUser__Select__Icon--open'] : ''
                }`}
              >
                <IconWrapper iconName="chevron-down" />
              </div>
            </div>

            {isDropdownOpen && (
              <div className={styles.AdminUser__Select__Dropdown}>
                {roleOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`${styles.AdminUser__Select__Option} ${
                      role === option.value
                        ? styles['AdminUser__Select__Option--selected']
                        : ''
                    } ${
                      option.disabled
                        ? styles['AdminUser__Select__Option--disabled']
                        : ''
                    }`}
                    onClick={() =>
                      !option.disabled && handleSelectRole(option.value)
                    }
                  >
                    <span className={styles.AdminUser__Select__Option__Title}>
                      {option.title}
                    </span>
                    <span
                      className={styles.AdminUser__Select__Option__Description}
                    >
                      {option.description}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Message for Administrator role */}
          {showAdminMessage && (
            <p
              className={styles.AdminUser__Subtitle}
              style={{ marginTop: 16, marginBottom: 16 }}
            >
              {t('drawers.adminUpdateUser.messages.administrator', {
                defaultValue:
                  'Gli utenti di tipo amministratore possono accedere a tutte le comunità',
              })}
            </p>
          )}

          {/* Message for End User role */}
          {showEnduserMessage && (
            <p
              className={styles.AdminUser__Subtitle}
              style={{ marginTop: 16, marginBottom: 16 }}
            >
              {t('drawers.adminUpdateUser.messages.enduser', {
                defaultValue:
                  'Gli utenti di tipo end user possono accedere alle comunità delle quali fanno parte i loro siti',
              })}
            </p>
          )}

          {/* Communities table for Supervisor role */}
          {showCommunitiesTable && (
            <AssignCommunitiesTable
              communities={filteredCommunities}
              counts={countsByCommunity}
              selectedIds={selectedCommunities}
              isAllSelected={isAllSelected}
              onToggleRow={toggleRow}
              onToggleAll={toggleSelectAll}
              onRangeToggle={toggleRange}
              search={search}
              onSearchChange={(v) => setSearch(v)}
              labels={{
                title: t('drawers.adminUpdateUser.inputs.assign.title', {
                  defaultValue: 'Assegna Community',
                }) as string,
                selectAll: (isAllSelected
                  ? t('drawers.adminUpdateUser.assign.deselectAll', {
                      defaultValue: 'Deseleziona tutto',
                    })
                  : t('drawers.adminUpdateUser.assign.selectAll', {
                      defaultValue: 'Seleziona tutto',
                    })) as string,
                searchPlaceholder: t(
                  'drawers.adminUpdateUser.inputs.assign.searchPlaceholder',
                  {
                    defaultValue: 'Cerca comunità',
                  }
                ) as string,
                selectedCountText: t(
                  'drawers.adminUpdateUser.inputs.assign.selectedCount',
                  {
                    count: selectedCommunities.size,
                    defaultValue: `${selectedCommunities.size} selezionate`,
                  }
                ) as string,
              }}
              classes={{
                wrapper: styles.AdminUser__Assign,
                header: styles.AdminUser__Assign__Header,
                headerActions: styles.AdminUser__Assign__Header__Actions,
                selectAll: styles.AdminUser__Assign__SelectAll,
                search: styles.AdminUser__Assign__Search,
                searchIcon: styles.AdminUser__Assign__Search__Icon,
                list: styles.AdminUser__Assign__List,
                listWrapper: styles.AdminUser__Assign__List__Wrapper,
                row: styles.AdminUser__Assign__Row,
                rowChecked: styles['AdminUser__Assign__Row--checked'],
                cellName: styles.AdminUser__Assign__Cell__Name,
                cellMuted: styles.AdminUser__Assign__Cell__Muted,
                cellCheck: styles.AdminUser__Assign__Cell__Check,
                footer: styles.AdminUser__Assign__Footer,
              }}
            />
          )}
        </div>
      </div>

      <div ref={footerRef} className={styles.AdminUser__Footer}>
        <Button
          variant="secondary"
          color="negative"
          label={
            (isInvited
              ? t('drawers.adminUpdateUser.footer.deleteInvited', {
                  defaultValue: 'Cancel invite',
                })
              : t('drawers.adminUpdateUser.footer.delete', {
                  defaultValue: 'Elimina utente',
                })) as string
          }
          style={{ marginRight: 'auto' }}
          className={styles.AdminUser__Footer__Button}
          onClick={() => {
            // userId is at root level, not in _raw. For admin-only users (Clerk), use clerkUserId.
            const userId =
              currentAdmin?.userId ||
              currentAdmin?.clerkUserId ||
              currentAdmin?._raw?.userId ||
              currentAdmin?._raw?.clerkUserId;
            const organizationId =
              metadata?.selectedOrganization?.organizationId ||
              ENV.VITE_APP_ORG_ID;

            if (!userId) {
              console.error('Cannot delete: userId not found', currentAdmin);
              return;
            }

            // Save data for the alert to use
            alertCallbackStore.setData({
              userId,
              organizationId,
            });

            dispatch(
              ALERT.SetAlert({
                value: {
                  type: 'ALERT_REMOVE_USER',
                },
              } as any)
            );
          }}
        />
        {isInvited && (
          <Button
            variant="secondary"
            color="primary"
            label={
              isResending ? (
                <Player
                  autoplay
                  loop
                  src={loader}
                  style={{ height: 18, transform: 'scale(0.7)' }}
                />
              ) : (
                (t('drawers.adminUpdateUser.footer.resend', {
                  defaultValue: 'Reinvia invito',
                }) as string)
              )
            }
            className={`${styles.AdminUser__Footer__Button} mr-1`}
            onClick={handleResendInvitation}
            disabled={isResending || isSubmitting}
          />
        )}
        <Button
          color="secondary"
          label={
            isSubmitting ? (
              <Player
                autoplay
                loop
                src={loader}
                style={{ height: 20, transform: 'scale(0.8)' }}
              />
            ) : (
              (t('drawers.adminUpdateUser.footer.save', {
                defaultValue: 'Salva',
              }) as string)
            )
          }
          className={styles.AdminUser__Footer__Button}
          onClick={handleSubmit}
          disabled={isSubmitting || !hasChanges}
        />
      </div>
    </>
  );
}

export default AdminUpdateUserDrawerContent;
